import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import Login from "./pages/Login";
import Layout from "./pages/Layout";
import Dashboard from "./pages/Dashboard";
import MyDay from "./pages/MyDay";
import ProgramBooking from "./pages/ProgramBooking";
import WeekendBooking from "./pages/WeekendBooking";
import Arrivals from "./pages/Arrivals";
import Sales from "./pages/Sales";
import Partners from "./pages/Partners";
import Team from "./pages/Team";
import ParticipantLogins from "./pages/ParticipantLogins";
import Trips from "./pages/Trips";
import Activities from "./pages/Activities";
import Issues from "./pages/Issues";
import Rooms from "./pages/Rooms";
import CForms from "./pages/CForms";
import Housekeeping from "./pages/Housekeeping";
import Settings from "./pages/Settings";
import NudgeThresholds from "./pages/NudgeThresholds";
import PartnerPaymentsOverview from "./pages/PartnerPaymentsOverview";
import PendingPayments from "./pages/PendingPayments";
import Content from "./pages/Content";
import Analytics from "./pages/Analytics";
import ReceiptsPayments from "./pages/ReceiptsPayments";
import Profile from "./pages/Profile";
import Reports from "./pages/Reports";
import Inventory from "./pages/Inventory";
import Attendance from "./pages/Attendance";
import MediaLog from "./pages/MediaLog";
import SuccessStories from "./pages/SuccessStories";
import PublicStories from "./pages/PublicStories";
import Book from "./pages/Book";
import Leads from "./pages/Leads";
import MarketingUpload from "./pages/MarketingUpload";
import ParticipantPortal from "./pages/ParticipantPortal";
import ParticipantOps from "./pages/ParticipantOps";
import StaffHub from "./pages/StaffHub";
import ApprovalRequests from "./pages/ApprovalRequests";
import PartnerAdmin from "./pages/PartnerAdmin";
import PartnerPortal from "./pages/PartnerPortal";
import Vendors from "./pages/Vendors";
import DestinationAdmin from "./pages/DestinationAdmin";
import WeekendTours from "./pages/WeekendTours";
import Pricing from "./pages/Pricing";
import PartnerPrograms from "./pages/PartnerPrograms";
import Chat from "./pages/Chat";
import PublicFeedback from "./pages/PublicFeedback";
import RecycleBin from "./pages/RecycleBin";
import SkipHistory from "./pages/SkipHistory";
import Onboarding from "./pages/Onboarding";
import { useParams } from "react-router-dom";
import { PartnerBrandProvider } from "./lib/brand";

/** Wrapper that reads `:slug` and injects a partner-specific brand
 *  context around the login screen so /p/global-volunteer-univ shows
 *  that partner's logo + hero copy above the login cards. */
function PartnerBrandedLogin() {
  const { slug } = useParams();
  return (
    <PartnerBrandProvider slug={slug}>
      <Login />
    </PartnerBrandProvider>
  );
}

const Private = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "PARTICIPANT") return <Navigate to="/portal" replace />;
  if (user.role === "PARTNER") return <Navigate to="/partner-portal" replace />;
  return children;
};

const ParticipantOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "PARTICIPANT") return <Navigate to="/dashboard" replace />;
  return children;
};

const PartnerOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "PARTNER") return <Navigate to="/dashboard" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/p/:slug" element={<PartnerBrandedLogin />} />
          <Route path="/stories" element={<PublicStories />} />
          <Route path="/book/:partnerId" element={<Book />} />
          <Route path="/marketing/leads-upload/:token" element={<MarketingUpload />} />
          <Route path="/feedback/:bookingId" element={<PublicFeedback />} />
          <Route path="/portal" element={<ParticipantOnly><ParticipantPortal /></ParticipantOnly>} />
          <Route path="/partner-portal" element={<PartnerOnly><PartnerPortal /></PartnerOnly>} />
          <Route path="/onboarding" element={<Private><Onboarding /></Private>} />
          <Route path="/" element={<Private><Layout /></Private>}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="staff-hub" element={<StaffHub />} />
            <Route path="approval-requests" element={<ApprovalRequests />} />
            <Route path="vendors" element={<Vendors />} />
            <Route path="partner-admin" element={<PartnerAdmin />} />
            <Route path="my-day" element={<MyDay />} />
            <Route path="program-bookings" element={<ProgramBooking />} />
            <Route path="weekend-bookings" element={<WeekendBooking />} />
            <Route path="weekend-tours" element={<WeekendTours />} />
            <Route path="pricing" element={<Pricing />} />
            <Route path="partner-programs" element={<PartnerPrograms />} />
            <Route path="chat" element={<Chat />} />
            <Route path="arrivals" element={<Arrivals />} />
            <Route path="trips" element={<Trips />} />
            <Route path="activities" element={<Activities />} />
            <Route path="issues" element={<Issues />} />
            <Route path="rooms" element={<Rooms />} />
            <Route path="cforms" element={<CForms />} />
            <Route path="housekeeping" element={<Housekeeping />} />
            <Route path="sales" element={<Sales />} />
            <Route path="partners" element={<Partners />} />
            <Route path="settings" element={<Settings />} />
            <Route path="nudge-thresholds" element={<NudgeThresholds />} />
            <Route path="partner-payments" element={<PartnerPaymentsOverview />} />
            <Route path="pending-payments" element={<PendingPayments />} />
            <Route path="destinations" element={<DestinationAdmin />} />
            <Route path="content" element={<Content />} />
            <Route path="content-analytics" element={<Analytics />} />
            <Route path="receipts-payments" element={<ReceiptsPayments />} />
            <Route path="reports" element={<Reports />} />
            <Route path="inventory" element={<Inventory />} />
            <Route path="attendance" element={<Attendance />} />
            <Route path="media" element={<MediaLog />} />
            <Route path="success-stories" element={<SuccessStories />} />
            <Route path="leads" element={<Leads />} />
            <Route path="participant-ops" element={<ParticipantOps />} />
            <Route path="profile" element={<Profile />} />
            <Route path="team" element={<Team />} />
            <Route path="participant-logins" element={<ParticipantLogins />} />
            <Route path="recycle-bin" element={<RecycleBin />} />
            <Route path="skip-history" element={<SkipHistory />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
